/** True when email is a masked display value from list/search APIs (e.g. j***@example.com). */
export function isMaskedEmail(value) {
  return String(value ?? "").includes("*");
}

/** Skip email format validators for masked values; keep required if present. */
export function getEmailValidationRules(validation = [], value) {
  if (!isMaskedEmail(value)) return validation;
  return validation.filter(
    (r) => r.name !== "emailNew" && r.name !== "email",
  );
}

/**
 * Resolve a full (unmasked) client email.
 * Prefer `candidateEmail` when unmasked; otherwise use `clientRecord.email`,
 * then optional `fetchClientById(clientId)`.
 */
export async function resolveUnmaskedClientEmail({
  candidateEmail,
  clientRecord,
  clientId,
  fetchClientById,
} = {}) {
  if (candidateEmail && !isMaskedEmail(candidateEmail)) {
    return String(candidateEmail).trim();
  }
  if (clientRecord?.email && !isMaskedEmail(clientRecord.email)) {
    return String(clientRecord.email).trim();
  }
  const id =
    clientId ||
    clientRecord?.client_id ||
    clientRecord?.id ||
    clientRecord?.billed_to;
  if (!id || typeof fetchClientById !== "function") {
    return candidateEmail ? String(candidateEmail).trim() : "";
  }
  try {
    const detail = await fetchClientById(id);
    if (detail?.email && !isMaskedEmail(detail.email)) {
      return String(detail.email).trim();
    }
  } catch {
    /* keep candidate */
  }
  return candidateEmail ? String(candidateEmail).trim() : "";
}

/**
 * Return invoice/contract-like data with an unmasked `bill_to_email` for
 * preview, print, and emailed PDF (never store/show masked Bill To).
 */
export async function withUnmaskedBillToEmail(invoice, fetchClientById) {
  if (!invoice || typeof invoice !== "object") return invoice;

  const current = invoice.bill_to_email ?? invoice.billToEmail ?? "";
  if (!isMaskedEmail(current)) return invoice;

  const clientId =
    invoice.billed_to ??
    invoice.bill_to ??
    invoice.client_id ??
    invoice.clientId ??
    invoice._invoiceRaw?.billed_to ??
    invoice._invoiceRaw?.client_id ??
    invoice._invoiceData?.billed_to ??
    invoice._invoiceData?.client_id;

  const resolved = await resolveUnmaskedClientEmail({
    candidateEmail: current,
    clientId,
    fetchClientById,
  });

  if (!resolved || isMaskedEmail(resolved)) return invoice;

  return {
    ...invoice,
    bill_to_email: resolved,
    billToEmail: resolved,
  };
}
